import { setIsLoading, setResponsePopup } from 'src/redux/slices/generalSlice';
import { AppThunk } from 'src/redux/store';
import {
  addAvailabilityBlock,
  createTherapistAction,
  deleteAvailabilityBlock,
  deleteTherapistAction,
  deleteTherapyProfilePictureAction,
  getMyAvailability,
  getMyAppointments,
  getTherapistDetailAction,
  getTherapyAvailability,
  getTherapyListAction,
  getTherapyQuestionsAction,
  saveTherapistAnswersRequest,
  updateAvailabilityBlock,
  updateTherapistStatusAction,
  updateTherapyAction,
  uploadTherapyPictureAction,
  UpdateTherapistAnswersRequest,
  getMeTherapist,
  uploadCurrenTherapistPictureAction,
  uploadSessionSummaryAction,
  getIntakeForm,
  getDownloadReport,
} from './requests';
import {
  addTherapyAvailability,
  createTherapist,
  deactivateActivateTherapist,
  deleteTherapistAvailability,
  deleteTherapy,
  deleteTherapyProfilePicture,
  setCurrentAvailability,
  setEditTherapistDetailView,
  setGetTherapyList,
  setPastSessions,
  setPastSessionsMetadata,
  setTherapistDetail,
  setTherapistDetailView,
  setTherapyAvailability,
  setTherapyQuestions,
  setUpcomingSessions,
  setUpcomingSessionsMetadata,
  updateTherapy,
  setMetadata,
  setCompletedOnboardingQuestions,
  setMyAvailability,
  setIntakeFormDtaa,
  setSummaryUpload,
  setSummaryDownloadData,
} from 'src/redux/slices/therapy.slice';
import {
  therapyGeneralInfo,
  NewAvailabilityBlock,
  UpdateAvailabilitySendData,
  AvailabilityBlock,
  TherapyAvailabilityNewI,
  QuestionAnswerPayload,
} from 'src/interfaces/therapyInterface';
import { setIsFirstLogin } from 'src/redux/slices/auth.slice';

export const getTherapyListUseCases =
  (therapyParams?: { Name?: string; page?: number; limit?: number }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getTherapyListAction(therapyParams)
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setGetTherapyList(res.data.data));
        dispatch(setMetadata(res.data.metadata));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

export const getTherapistDetailUseCase =
  (therapistId: string): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getTherapistDetailAction(therapistId)
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setTherapistDetail(res.data.data));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Create coach use case
export const createTherapyUseCase =
  (newCoach: therapyGeneralInfo): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await createTherapistAction(newCoach)
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(createTherapist(res.data));
        const idResponse = res.data.data.therapistId;
        localStorage.setItem('current-Therapist', JSON.stringify(idResponse));
        dispatch(setTherapistDetailView('additional-info'));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        } else if (err.response.status === 400) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: 'Unable to create a therapy with an existing user email.',
              submitBtn: 'Ok',
            }),
          );
        } else {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Delete therapist use case
export const deleteTherapistUseCase =
  (id: number): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await deleteTherapistAction({ id: id })
      .then((res) => {
        dispatch(setIsLoading(false));
        if (res.status == 200) {
          dispatch(deleteTherapy(Number(id)));
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'Therapist has been successfully deleted.',
              submitBtn: 'Ok',
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Deactivate/ activate therapy use case
export const updateTherapistStatusUseCase =
  (props: { therapistId: string | undefined; status: boolean }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await updateTherapistStatusAction({
      therapistId: props.therapistId,
      status: props.status,
    })
      .then((res) => {
        dispatch(setIsLoading(false));
        if (res.data.message === 'Therapist successfully deactivated.') {
          dispatch(
            deactivateActivateTherapist({
              therapistId: Number(props.therapistId),
              status: 'Deactivated',
            }),
          );
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'The therapist has been successfully deactivated.',
              submitBtn: 'Ok',
            }),
          );
        } else if (res.data.message === 'Therapist successfully activated.') {
          dispatch(
            deactivateActivateTherapist({
              therapistId: Number(props.therapistId),
              status: 'Registered',
            }),
          );
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'The therapist has been successfully activated.',
              submitBtn: 'Ok',
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Upload therapy profile picture use case
export const uploadTherapyPictureUseCase =
  (props: { therapistId?: string | undefined; profilePicture: FormData }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const res = await uploadTherapyPictureAction({
        therapistId: props.therapistId,
        profilePicture: props.profilePicture,
      });

      if (res.status === 200) {
        dispatch(setIsLoading(false));
        dispatch(setTherapistDetailView('finish'));
      } else if (res.status === 400) {
        dispatch(setIsLoading(false));
        dispatch(setTherapistDetailView(''));
        const errorMessage = 'An profile picture is in invalid format';
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            subtitle: errorMessage,
            submitBtn: 'Ok',
          }),
        );
      }
    } catch (err: any) {
      dispatch(setIsLoading(false));
      dispatch(setTherapistDetailView(''));

      const errorMessage =
        err.response?.data?.detail || 'An error occurred while uploading the picture';

      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: errorMessage,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Delete user profile picture use case
export const deleteTherapyProfilePictureUseCase =
  (therapistId: string | undefined): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await deleteTherapyProfilePictureAction(therapistId)
      .then((res) => {
        dispatch(setIsLoading(false));
        if (res.status === 200) {
          dispatch(deleteTherapyProfilePicture(therapistId));
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'The profile picture has been deleted.',
              submitBtn: 'Ok',
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Update coach use case
export const updateTherapyUseCase =
  (props: { therapistId: string | undefined; updatedTherapist: any }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await updateTherapyAction({
      therapistId: props.therapistId,
      updatedTherapist: props.updatedTherapist,
    })
      .then((res) => {
        dispatch(setIsLoading(false));
        if (res.status === 200) {
          dispatch(updateTherapy(props.updatedTherapist));
          dispatch(setEditTherapistDetailView('edit-additional-info'));
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'The therapist has been edited.',
              submitBtn: 'Ok',
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Get therapy new availability use case
export const getTherapyNewAvailabilityUseCase =
  (info: { therapistId: string | number | undefined }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getTherapyAvailability({
      therapistId: info.therapistId,
    })
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setTherapyAvailability(res.data.data));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Get therapy availability use case
export const getTherapyAvailabilityUseCase =
  (info: { therapistId: string | number | undefined }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getTherapyAvailability({
      therapistId: info.therapistId,
    })
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setTherapyAvailability(res.data.data));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

// Add coach new AvailabilityBlock use case
export const addNewAvailabilityBlockUseCase =
  (info: { therapistId: string; data: NewAvailabilityBlock }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const resp = await addAvailabilityBlock({ therapistId: info.therapistId, data: info.data });
      if (resp.status !== 200) {
        dispatch(setIsLoading(false));
        console.log('res not 200');
      }
      dispatch(setIsLoading(false));
      const res = resp.data.data;
      const createdAv = {
        startDate: res[0].date,
        endDate: res[res.length - 1].date,
        startHour: res[0].startHour,
        endHour: res[0].endHour,
        appointmentsCount: 0,
        availabilityList: resp.data.data,
      };
      dispatch(addTherapyAvailability(createdAv));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'success',
          header: 'Done!',
          subtitle: 'Availability slot created successfully!',
          submitBtn: 'Ok',
        }),
      );
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Add coach AvailabilityBlock use case
export const addAvailabilityBlockUseCase =
  (info: { therapistId: string; data: AvailabilityBlock }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const resp = await addAvailabilityBlock({ therapistId: info.therapistId, data: info.data });
      if (resp.status !== 200) {
        dispatch(setIsLoading(false));
        console.log('res not 200');
      }
      dispatch(setIsLoading(false));
      dispatch(addTherapyAvailability(resp.data.data));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'success',
          header: 'Done!',
          subtitle: 'Availability block created.',
          submitBtn: 'Ok',
        }),
      );
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Update AvailabilityBlock use case
export const updateAvailabilityBlockUseCase =
  (props: {
    therapistId: number | undefined;
    data: UpdateAvailabilitySendData;
    role: string;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const resp = await updateAvailabilityBlock({
        therapistId: props.therapistId,
        data: props.data,
      });
      if (resp.status !== 200) {
        dispatch(setIsLoading(false));
        console.log('res not 200');
      }
      if (resp.status === 200) {
        dispatch(setIsLoading(false));

        dispatch(getTherapyNewAvailabilityUseCase({ therapistId: props.therapistId?.toString() }));
        {
          props.role === 'therapist' && dispatch(getMyAvailabilityUseCase());
        }
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'success',
            header: 'Done!',
            subtitle: 'Availability slot edited successfully!',
            submitBtn: 'Ok',
          }),
        );
      }
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Delete availability use Case
export const deleteAvailabilityBlockUseCase =
  (props: {
    therapisId: string | undefined;
    availabilityList: [];
    firstAvailabilityIdToDelete: number;
    lastAvailabilityIdToDelete: number;
    data: TherapyAvailabilityNewI;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const resp = await deleteAvailabilityBlock({
        therapistId: props.therapisId,
        data: {
          firstAvailabilityIdToDelete: props.firstAvailabilityIdToDelete,
          lastAvailabilityIdToDelete: props.lastAvailabilityIdToDelete,
        },
      });
      if (resp.status !== 200) {
        dispatch(setIsLoading(false));
      }
      dispatch(setIsLoading(false));
      if (resp.status === 200) {
        dispatch(setIsLoading(false));
        if (resp.data.message === 'Availability successfully removed.') {
          dispatch(deleteTherapistAvailability(props.data));
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'Availability slot has been deleted successfully!',
              submitBtn: 'Ok',
            }),
          );
          dispatch(setCurrentAvailability(''));
        }
      }
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Delete availability when editing
export const editSpecificDeleteAvBlockUseCase =
  (props: {
    therapistId: string | undefined;
    firstAvailabilityIdToDelete: number;
    lastAvailabilityIdToDelete: number;
  }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const resp = await deleteAvailabilityBlock({
        therapistId: props.therapistId,
        data: {
          firstAvailabilityIdToDelete: props.firstAvailabilityIdToDelete,
          lastAvailabilityIdToDelete: props.lastAvailabilityIdToDelete,
        },
      });
      if (resp.status !== 200) {
        dispatch(setIsLoading(false));
        console.log('res not 200');
      }
      dispatch(setIsLoading(false));
      if (resp.status === 200) {
        dispatch(setIsLoading(false));
        if (resp.data.message === 'Availability successfully removed.') {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'success',
              header: 'Done!',
              subtitle: 'Availability slot has been removed successfully!',
              submitBtn: 'Ok',
            }),
          );
          dispatch(setCurrentAvailability(''));
        }
      }
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    }
  };

// Get my availability
export const getMyAvailabilityUseCase = (): AppThunk => async (dispatch) => {
  dispatch(setIsLoading(true));
  const data = await getMyAvailability()
    .then((res) => {
      dispatch(setIsLoading(false));
      dispatch(setMyAvailability(res.data.data));
    })
    .catch((err: any) => {
      dispatch(setIsLoading(false));
      console.log(err);
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: err.response.data.detail,
          submitBtn: 'Ok',
        }),
      );
    });
  return data;
};

export const getTherapyQuestionsUseCase = (): AppThunk => async (dispatch) => {
  dispatch(setIsLoading(true));
  const data = await getTherapyQuestionsAction()
    .then((res) => {
      dispatch(setIsLoading(false));
      dispatch(setTherapyQuestions(res?.data?.data));
    })
    .catch((err) => {
      dispatch(setIsLoading(false));
      if (err.response?.data?.status === 409) {
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            // subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      }
      return [];
    });
  return data;
};

export const saveTherapistAnswersUseCase =
  (props: { data: QuestionAnswerPayload[] }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));

    const data = await saveTherapistAnswersRequest({ data: props.data })
      .then((res) => {
        console.log(res);
        dispatch(setIsLoading(false));
        dispatch(setIsFirstLogin(false));
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'success',
            header: 'Done!',
            subtitle: 'Answers saved successfully!',
            submitBtn: 'Ok',
          }),
        );
        dispatch(setCompletedOnboardingQuestions('completed'));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      });
    return data;
  };

export const updateTherapistAnswersUseCase =
  (props: { therapistId: any; data: QuestionAnswerPayload[] }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await UpdateTherapistAnswersRequest({
      therapistId: props.therapistId,
      data: props.data,
    })
      .then((res: any) => {
        console.log(res);
        dispatch(setIsLoading(false));
        dispatch(setIsFirstLogin(false));
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'success',
            header: 'Done!',
            subtitle: 'Answers updated successfully!',
            submitBtn: 'Ok',
          }),
        );
        dispatch(setCompletedOnboardingQuestions('completed'));
      })
      .catch((err: { response: { data: { detail: any } } }) => {
        dispatch(setIsLoading(false));
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      });
    return data;
  };

// Get past sessions
export const getPastSessionsUseCase =
  (page: number, endDateTime: string, isDescendingOrder: boolean): AppThunk =>
  async (dispatch) => {
    return getMyAppointments({ page, endDateTime, isDescendingOrder })
      .then((res) => {
        // console.log(res);
        dispatch(setPastSessions(res.data.data));
        dispatch(setPastSessionsMetadata(res.data.metadata));
      })
      .catch((err: any) => {
        console.log(err);
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      });
  };

// Get upcoming sessions
export const getUpcomingSessionsUseCase =
  (page: number, startDateTime: string, isDescendingOrder: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getMyAppointments({ page, startDateTime, isDescendingOrder })
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setUpcomingSessions(res.data.data));
        dispatch(setUpcomingSessionsMetadata(res.data.metadata));
      })
      .catch((err: any) => {
        dispatch(setIsLoading(false));
        console.log(err);
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            // subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      });
    return data;
  };

// Upload session summary use case
export const uploadSessionSummaryUseCase =
  (props: { appointmentId: string | undefined; file: File | null; comment: string }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));

    try {
      const formData = new FormData();

      if (props.file) {
        formData.append('sessionSummary', props.file);
      }

      const res = await uploadSessionSummaryAction({
        appointmentId: props.appointmentId,
        sessionSummary: formData,
        comment: props.comment || '',
      });

      if (res.status === 200) {
        dispatch(setIsLoading(false));
        dispatch(setSummaryUpload(true));

        // You might want to update some state in your therapy slice here
        // dispatch(updateSessionWithSummary(props.sessionId));
      }

      return res.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(
        setResponsePopup({
          isVisible: true,
          type: 'error',
          header: 'Error',
          subtitle: error.response?.data?.detail || 'Failed to upload session summary',
          submitBtn: 'Ok',
        }),
      );

      throw error;
    }
  };

// Get Business users list use case
export const TherapistMeUseCase = (): AppThunk => async (dispatch) => {
  dispatch(setIsLoading(true));
  try {
    const res = await getMeTherapist();
    dispatch(setIsLoading(false));
    dispatch(setTherapistDetail(res.data.data));
    dispatch(setMetadata(res.data.metadata));
  } catch (err: any) {
    dispatch(setIsLoading(false));
    dispatch(
      setResponsePopup({
        isVisible: true,
        type: 'error',
        header: 'Error',
        subtitle: err.response?.data?.detail,
        submitBtn: 'Ok',
      }),
    );
  }
};
// Upload curent coach profile picture
export const uploadCurrentTherapistPictureUseCase =
  (props: { profilePicture: FormData }): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await uploadCurrenTherapistPictureAction(props)
      .then(() => {
        dispatch(setIsLoading(false));
        dispatch(setEditTherapistDetailView('finish'));
      })
      .catch((err: any) => {
        dispatch(setIsLoading(false));
        console.log(err);
        dispatch(
          setResponsePopup({
            isVisible: true,
            type: 'error',
            header: 'Error',
            subtitle: err.response.data.detail,
            submitBtn: 'Ok',
          }),
        );
      });
    return data;
  };

export const getIntakeFormUsecases =
  (appointmentId: any): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getIntakeForm(appointmentId)
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setIntakeFormDtaa(res.data));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };

export const getSummaryReportUsecase =
  (appointmentId: any): AppThunk =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const data = await getDownloadReport(appointmentId)
      .then((res) => {
        dispatch(setIsLoading(false));
        dispatch(setSummaryDownloadData(res.data));
      })
      .catch((err) => {
        dispatch(setIsLoading(false));
        if (err.response.data.status === 409) {
          dispatch(
            setResponsePopup({
              isVisible: true,
              type: 'error',
              header: 'Error',
              subtitle: err.response.data.detail,
              submitBtn: 'Ok',
            }),
          );
        }
      });
    return data;
  };
